import { useState, useEffect, useCallback } from 'react';
import { Loader, Trash2, MessageSquare, Send, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PartnershipComment } from '../../types/partnership';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { usernameCacheService } from '../../services/usernameCache';
import { CreatorProfile } from '../CreatorProfile';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface PartnershipCommentsSectionProps {
  partnershipId: string;
  canCreate: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

export function PartnershipCommentsSection({
  partnershipId,
  canCreate,
  canDelete,
  onUpdate,
}: PartnershipCommentsSectionProps) {
  const [comments, setComments] = useState<PartnershipComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getCommentsForPartnership(partnershipId);
      const sorted = [...data].sort((a, b) => a.created_at - b.created_at);
      setComments(sorted);
      const authorIds = sorted.map((c) => c.author_id).filter(Boolean);
      usernameCacheService.prefetchUsernames(authorIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [partnershipId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = newContent.trim();
    if (!content || !canCreate || submitting) return;
    setSubmitting(true);
    try {
      await partnershipsApiService.createComment(partnershipId, content);
      setNewContent('');
      await loadComments();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!canDelete || deletingId) return;
    const confirmed = window.confirm('Are you sure you want to delete this comment?');
    if (!confirmed) return;
    setDeletingId(commentId);
    try {
      await partnershipsApiService.deleteComment(commentId);
      await loadComments();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (ts: number) => {
    const date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <SkeletonLoader className="h-4 w-28" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="subcard p-3 space-y-2">
            <div className="flex items-center gap-2">
              <SkeletonLoader className="w-6 h-6 rounded-md" />
              <SkeletonLoader className="h-3 w-24" />
              <SkeletonLoader className="h-3 w-16" />
            </div>
            <SkeletonLoader className="h-3 w-full" />
            <SkeletonLoader className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load comments"
        description={error}
        action={
          <button
            type="button"
            onClick={loadComments}
            className="glass-button"
          >
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Comments" icon={MessageSquare} count={comments.length} />

      <div className="space-y-2">
        {comments.length === 0 ? (
          <EmptyState
            bare
            compact
            icon={MessageSquare}
            title="No comments yet"
            description="Discussion with the team about this partnership will appear here."
          />
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className="subcard p-3 flex items-start gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CreatorProfile creatorId={comment.author_id} inline />
                  <span className="text-xs text-glass-500 tabular-nums">{formatDate(comment.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-glass-200 whitespace-pre-wrap">{comment.content}</p>
              </div>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => handleDelete(comment.id)}
                  disabled={deletingId === comment.id}
                  className={cn(
                    'btn-icon btn-icon-sm text-red-400 hover:text-red-300 flex-shrink-0',
                    deletingId === comment.id && 'opacity-50 cursor-not-allowed'
                  )}
                  title="Delete comment"
                  aria-label="Delete comment"
                >
                  {deletingId === comment.id ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {canCreate && (
        <form onSubmit={handleSubmit} className="space-y-2 pt-3 border-t border-glass-200/10">
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Write a comment…"
            rows={2}
            className="glass-input w-full resize-none"
            disabled={submitting}
            aria-label="New comment"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!newContent.trim() || submitting}
              className="btn-primary"
            >
              {submitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Post
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
